import Constants from 'expo-constants';
import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { submitFeedbackApi } from '@/api/board-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { useAppMetaStore } from '@/store/app-meta-store';
import { colors, radius, spacing } from '@/theme/tokens';

/**
 * In-app rating — mock: oce_aplikacj_*. INTENT: catch the "happy moment" right
 * after a successful policy add and route it well. 4-5★ → straight to the store
 * (no second confirm button) so the good review lands where it counts; coming
 * back they find a referral nudge (50 zł voucher) — a happy rater is the best
 * moment to ask for a recommendation. Under 4★ → we KEEP it in-app: a comment
 * box, feedback comes to us, not to a public 1-star review. We only ever
 * prompt once (ratingDone).
 */

// Store IDs — the Android package is real (app.json); the iOS App Store id is a
// placeholder until the app is published. openStore() no-ops on web/dev.
const APP_STORE_ID = '0000000000';

function openStore() {
  if (Platform.OS === 'web') return;
  const pkg = Constants.expoConfig?.android?.package ?? 'pl.polfinance.opiekunrodziny';
  const url =
    Platform.OS === 'ios'
      ? `itms-apps://itunes.apple.com/app/id${APP_STORE_ID}`
      : `market://details?id=${pkg}`;
  const webFallback =
    Platform.OS === 'ios'
      ? `https://apps.apple.com/app/id${APP_STORE_ID}`
      : `https://play.google.com/store/apps/details?id=${pkg}`;
  Linking.openURL(url).catch(() => Linking.openURL(webFallback).catch(() => undefined));
}

function close() {
  if (router.canGoBack()) router.back();
  else router.replace('/(tabs)');
}

export default function RateScreen() {
  const { t } = useTranslation();
  const setRating = useAppMetaStore((s) => s.setRating);
  const dismissRating = useAppMetaStore((s) => s.dismissRating);

  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  // After a 4-5★ rating the screen stays behind the store sheet with a referral
  // nudge — a freshly happy user is the one most likely to recommend us (CEO ask).
  const [thanks, setThanks] = useState(false);

  const lowRating = stars > 0 && stars < 4;

  const appVersion = Constants.expoConfig?.version;

  const onStar = (value: number) => {
    setStars(value);
    if (value >= 4) {
      // Happy path: record it, send them to the store — the referral card waits
      // for them when they come back (close() only via its dismiss link).
      void setRating(value);
      void submitFeedbackApi(value, null, appVersion); // also log the score to us
      openStore();
      setThanks(true);
    }
    // Low rating: stay on screen; the comment box below appears.
  };

  const sendFeedback = () => {
    // Feedback comes to US (not a public store review). Persist locally so nothing
    // is lost, and deliver to the backend inbox when a server is configured.
    const text = comment.trim() || null;
    void setRating(stars, text);
    void submitFeedbackApi(stars, text, appVersion);
    close();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, flexGrow: 1, justifyContent: 'center' }}
        >
          <Card style={{ gap: spacing.md, alignItems: 'center', paddingVertical: spacing.xl }}>
            {thanks ? (
              /* 4-5★ follow-up: thanks + referral nudge (50 zł fuel voucher). */
              <>
                <View
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: radius.full,
                    backgroundColor: colors.primaryTile,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="gift-outline" size={44} color={colors.navy} />
                </View>
                <AppText variant="headlineMd" style={{ textAlign: 'center' }}>
                  {t('rate.thanksTitle')}
                </AppText>
                <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
                  {t('rate.thanksBody')}
                </AppText>
                <PrimaryButton
                  label={t('rate.thanksCta')}
                  onPress={() => router.replace('/refer')}
                  style={{ alignSelf: 'stretch' }}
                />
                <Pressable
                  onPress={close}
                  accessibilityRole="button"
                  style={{ minHeight: 44, justifyContent: 'center' }}
                >
                  <AppText variant="labelLg" color={colors.outline}>
                    {t('rate.thanksSkip')}
                  </AppText>
                </Pressable>
              </>
            ) : !lowRating ? (
              <>
                <View
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: radius.full,
                    backgroundColor: colors.primaryTile,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="heart-outline" size={44} color={colors.navy} />
                </View>
                <AppText variant="headlineMd" style={{ textAlign: 'center' }}>
                  {t('rate.title')}
                </AppText>
                <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
                  {t('rate.subtitle')}
                </AppText>
              </>
            ) : (
              <AppText variant="headlineSm" style={{ textAlign: 'center' }}>
                {t('rate.lowTitle')}
              </AppText>
            )}

            {/* Stars */}
            {!thanks && (
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Pressable key={i} accessibilityRole="button" onPress={() => onStar(i)} hitSlop={6}>
                    <Ionicons
                      name={i <= stars ? 'star' : 'star-outline'}
                      size={44}
                      color={i <= stars ? '#F5A623' : colors.outlineVariant}
                    />
                  </Pressable>
                ))}
              </View>
            )}

            {lowRating && (
              <View style={{ alignSelf: 'stretch', gap: spacing.md }}>
                <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
                  {t('rate.lowSubtitle')}
                </AppText>
                <TextField
                  placeholder={t('rate.commentPlaceholder')}
                  value={comment}
                  onChangeText={setComment}
                  multiline
                  style={{ minHeight: 120, textAlignVertical: 'top', paddingTop: spacing.sm }}
                />
                <PrimaryButton label={t('rate.send')} onPress={sendFeedback} />
              </View>
            )}

            {!lowRating && !thanks && (
              <Pressable
                onPress={() => {
                  void dismissRating();
                  close();
                }}
                accessibilityRole="button"
                style={{ minHeight: 44, justifyContent: 'center' }}
              >
                <AppText variant="labelLg" color={colors.outline}>
                  {t('rate.later')}
                </AppText>
              </Pressable>
            )}
          </Card>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
